import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  FileText, 
  Clock, 
  CheckCircle, 
  XCircle, 
  Eye,
  Calendar,
  DollarSign,
  RefreshCw,
  Check,
  X,
  ChevronDown,
  ChevronUp,
  History,
  Send,
  AlertCircle
} from 'lucide-react';
import MobileLayout from '../layouts/MobileLayout';
import { useAuth } from '../../components/AuthProvider';
import { purchaseRequestApiService, PurchaseRequest } from '../../services/purchaseRequestApi';
import { promotionApiService } from '../../services/promotionApi';

const MobileFacturesPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [invoices, setInvoices] = useState<PurchaseRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [processingAction, setProcessingAction] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [expandedInvoice, setExpandedInvoice] = useState<number | null>(null);

  const loadInvoices = useCallback(async () => {
    try {
      setLoading(true);
      if (!user?.id) return;
      
      const data = await purchaseRequestApiService.getUserPurchaseRequests();
      setInvoices(data);
    } catch (error) {
      console.error('Erreur chargement factures:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadInvoices();
  }, [loadInvoices]);

  const confirmInvoice = async (invoiceId: number) => {
    try {
      setProcessingAction(invoiceId.toString());
      
      const invoice = invoices.find(inv => inv.id === invoiceId);
      
      // Désactiver le code promo s'il existe
      if (invoice?.promo_code) {
        try {
          await promotionApiService.deactivateUserPromoCode(invoice.promo_code);
        } catch (promoError) {
          console.error('Erreur désactivation promo:', promoError);
        }
      }
      
      await purchaseRequestApiService.updatePurchaseRequestStatus(invoiceId.toString(), 'confirmed');
      await loadInvoices();
      alert('✅ Facture confirmée avec succès !');
    } catch (error) {
      console.error('Erreur confirmation:', error);
      alert('Erreur lors de la confirmation');
    } finally {
      setProcessingAction(null);
    }
  };

  const cancelInvoice = async (invoiceId: number) => {
    if (!window.confirm('Annuler cette facture ?')) return;

    try {
      setProcessingAction(invoiceId.toString());
      
      const invoice = invoices.find(inv => inv.id === invoiceId);
      
      const wasEverConfirmed = invoice?.timeline?.some(
        (entry: any) => entry.action === 'client_confirmation'
      ) || false;
      
      await purchaseRequestApiService.updatePurchaseRequestStatus(invoiceId.toString(), 'cancelled');
      
      // Réactiver le promo si jamais confirmée
      if (invoice?.promo_code && !wasEverConfirmed) {
        try {
          await promotionApiService.reactivatePromoCode(invoice.promo_code);
        } catch (promoError) {
          console.error('Erreur réactivation promo:', promoError);
        }
      }
      
      await loadInvoices();
      alert('❌ Facture annulée');
    } catch (error) {
      console.error('Erreur annulation:', error);
      alert('Erreur lors de l\'annulation');
    } finally {
      setProcessingAction(null);
    }
  };

  const getStatusInfo = (status: string) => {
    switch (status) {
      case 'pending':
        return {
          label: 'En attente',
          color: 'bg-amber-100 text-amber-800',
          icon: Clock,
          bgColor: 'bg-amber-500'
        };
      case 'revision':
        return {
          label: 'Révisée',
          color: 'bg-blue-100 text-blue-800',
          icon: RefreshCw,
          bgColor: 'bg-blue-500'
        };
      case 'confirmed':
        return {
          label: 'Confirmée',
          color: 'bg-green-100 text-green-800',
          icon: CheckCircle,
          bgColor: 'bg-green-500'
        };
      case 'cancelled':
        return {
          label: 'Annulée',
          color: 'bg-red-100 text-red-800',
          icon: XCircle,
          bgColor: 'bg-red-500'
        };
      default:
        return {
          label: 'Inconnu',
          color: 'bg-gray-100 text-gray-800',
          icon: AlertCircle,
          bgColor: 'bg-gray-500'
        };
    }
  };

  const formatPrice = (price: number) => {
    return `${price.toLocaleString('fr-FR')} FCFA`;
  };

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return 'N/A';
    
    let isoDateString = dateString;
    if (dateString.includes(' ') && !dateString.includes('T')) {
      isoDateString = dateString.replace(' ', 'T') + 'Z';
    }
    
    const date = new Date(isoDateString);
    if (isNaN(date.getTime())) return 'N/A';
    
    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getTimelineSteps = (invoice: PurchaseRequest) => {
    if (invoice.timeline && invoice.timeline.length > 0) {
      return invoice.timeline.map((entry, index) => {
        let icon = Clock;
        if (entry.action === 'created') icon = Send;
        else if (entry.action === 'admin_revision') icon = RefreshCw;
        else if (entry.action === 'client_confirmation') icon = CheckCircle;
        else if (entry.action === 'client_cancellation') icon = XCircle;
        
        let title = 'Étape';
        if (entry.action === 'created') title = 'Demande envoyée';
        else if (entry.action === 'admin_revision') title = 'Facture révisée';
        else if (entry.action === 'client_confirmation') title = 'Confirmée';
        else if (entry.action === 'client_cancellation') title = 'Annulée';

        return {
          title,
          date: entry.date,
          icon,
          isRevision: entry.action === 'admin_revision'
        };
      });
    }

    return [{
      title: 'Demande envoyée',
      date: invoice.created_at || new Date().toISOString(),
      icon: Send,
      isRevision: false
    }];
  };

  const filteredInvoices = invoices.filter(invoice => {
    return statusFilter === 'all' || invoice.status === statusFilter;
  });

  if (loading) {
    return (
      <MobileLayout>
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Chargement...</p>
          </div>
        </div>
      </MobileLayout>
    );
  }

  return (
    <MobileLayout>
      {/* Header */}
      <div className="bg-gradient-to-br from-amber-500 via-amber-600 to-orange-600 px-4 py-6 sticky top-0 z-10 shadow-lg">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-white/90 hover:text-white mb-3"
        >
          <ArrowLeft className="w-5 h-5" />
          <span className="text-sm">Retour</span>
        </button>
        
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-white mb-1">Mes Factures</h1>
            <p className="text-sm text-white/90">{filteredInvoices.length} facture(s)</p>
          </div>
          
          <button
            onClick={loadInvoices}
            disabled={loading}
            className="p-2 bg-white/20 rounded-lg text-white disabled:opacity-50"
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {/* Filtre */}
        <div className="mt-4">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="w-full px-3 py-2 bg-white/20 backdrop-blur-sm text-white rounded-lg text-sm border border-white/30 focus:outline-none focus:bg-white/30"
          >
            <option value="all" className="text-gray-900">Tous les statuts</option>
            <option value="pending" className="text-gray-900">En attente</option>
            <option value="revision" className="text-gray-900">Révisée</option>
            <option value="confirmed" className="text-gray-900">Confirmée</option>
            <option value="cancelled" className="text-gray-900">Annulée</option>
          </select>
        </div>
      </div>

      {/* Liste des factures */}
      <div className="px-4 py-6 space-y-4">
        {filteredInvoices.length === 0 ? (
          <div className="bg-white rounded-2xl p-8 text-center shadow-sm">
            <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Aucune facture</h3>
            <p className="text-sm text-gray-500">
              {statusFilter !== 'all' 
                ? 'Aucune facture avec ce statut.'
                : 'Vous n\'avez pas encore de factures.'
              }
            </p>
          </div>
        ) : (
          filteredInvoices.map((invoice) => {
            const statusInfo = getStatusInfo(invoice.status);
            const StatusIcon = statusInfo.icon;
            const timelineSteps = getTimelineSteps(invoice);
            const isExpanded = expandedInvoice === invoice.id;
            const lastRevisionStep = timelineSteps.filter(s => s.isRevision).pop();

            return (
              <div key={invoice.id} className="bg-white rounded-2xl shadow-sm overflow-hidden">
                {/* En-tête */}
                <div className="p-4">
                  <div className="flex items-start gap-3 mb-3">
                    <div className={`w-10 h-10 rounded-lg ${statusInfo.bgColor} flex items-center justify-center flex-shrink-0`}>
                      <StatusIcon className="w-5 h-5 text-white" />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <h3 className="font-bold text-gray-900 text-sm">
                            Facture #{invoice.invoice_id || invoice.id}
                          </h3>
                          <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-medium ${statusInfo.color}`}>
                            {statusInfo.label}
                          </span>
                        </div>
                      </div>
                      
                      <div className="mt-2 space-y-1 text-xs text-gray-600">
                        <div className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          <span>{formatDate(invoice.created_at)}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <DollarSign className="w-3 h-3" />
                          <span className="font-bold text-amber-600">
                            {formatPrice(invoice.total_amount || 0)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2">
                    {invoice.status === 'revision' && (
                      <>
                        <button
                          onClick={() => invoice.id && confirmInvoice(invoice.id)}
                          disabled={processingAction === invoice.id?.toString()}
                          className="flex-1 py-2 bg-green-600 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-50"
                        >
                          <Check className="w-4 h-4" />
                          Confirmer
                        </button>
                        <button
                          onClick={() => invoice.id && cancelInvoice(invoice.id)}
                          disabled={processingAction === invoice.id?.toString()}
                          className="flex-1 py-2 bg-red-600 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-50"
                        >
                          <X className="w-4 h-4" />
                          Annuler
                        </button>
                      </>
                    )}

                    {invoice.status === 'cancelled' && (
                      <button
                        onClick={() => invoice.id && confirmInvoice(invoice.id)}
                        disabled={processingAction === invoice.id?.toString()}
                        className="flex-1 py-2 bg-green-600 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-50"
                      >
                        <Check className="w-4 h-4" />
                        Confirmer
                      </button>
                    )}

                    {lastRevisionStep && invoice.id && (
                      <button
                        onClick={() => navigate('/facture-proforma', { 
                          state: { 
                            purchaseRequestId: invoice.id!.toString(),
                            viewMode: true,
                            invoiceData: invoice
                          }
                        })}
                        className="flex-1 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-2"
                      >
                        <Eye className="w-4 h-4" />
                        Voir
                      </button>
                    )}
                    
                    <button
                      onClick={() => setExpandedInvoice(isExpanded ? null : invoice.id || null)}
                      className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg"
                    >
                      {isExpanded ? (
                        <ChevronUp className="w-4 h-4" />
                      ) : (
                        <ChevronDown className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </div>

                {/* Historique expandable */}
                {isExpanded && (
                  <div className="border-t border-gray-200 bg-gray-50 p-4">
                    <h4 className="text-xs font-bold text-gray-900 mb-3 flex items-center gap-2">
                      <History className="w-3 h-3" />
                      Historique
                    </h4>
                    <div className="space-y-3">
                      {timelineSteps.map((step, index) => {
                        const StepIcon = step.icon;
                        const isLast = index === timelineSteps.length - 1;
                        
                        return (
                          <div key={index} className="flex items-start gap-2 relative">
                            <div className="w-6 h-6 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                              <StepIcon className="w-3 h-3 text-amber-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between gap-2">
                                <p className="text-xs font-semibold text-gray-900">{step.title}</p>
                                <p className="text-[10px] text-gray-500 flex-shrink-0">
                                  {formatDate(step.date)}
                                </p>
                              </div>
                            </div>
                            {!isLast && (
                              <div className="absolute left-3 top-6 w-px h-3 bg-gray-300" />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </MobileLayout>
  );
};

export default MobileFacturesPage;
